import React, { useCallback, useEffect, useRef, useState } from 'react';
import { capitalize, noop, OSKGeoJson, OSKSceneImagingMode } from 'oskcore';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Checkbox, DatePicker, OSKIcon, OSKThemeType, Slider, Typography } from 'oskcomponents';
import { useDisableFeatureOnMouseOver, useMap } from '~/hooks';
import { connect, useDispatch } from 'react-redux';
import { GlobalZIndex } from 'oskcomponents/src/constants';
import GeocodingSearch from '../GeocodingSearch';
import { useTheme } from 'styled-components';
import { ListEntry } from 'oskcomponents';
import { Sensor } from 'oskcore';
import { RootState } from '../../redux/store';
import { DropdownFilterPanel } from './DropdownFilterPanel';
import { FilterPanelButton, FilterPanelButtonHandle } from './FilterPanelButton';
import {
    excludePlatform,
    includePlatform,
    setDateRange,
    NullableDate,
    setIsSearchDirty,
    setCloudCoverFilter,
    doSearchAsyncForTaskId,
    setSunAzimuthFilter,
    setSunElevationFilter,
    setOffNadirFilter,
    setViewAzimuthFilter,
    setImagingModeFilter,
    setPurchasedItemsFilter,
} from '~/redux/modules/data/search';

import { LatLng } from 'leaflet';
import * as L from 'leaflet';
import { date_format, ensureUUIDFormat } from '~/utils';
import { FaCloud, FaSun } from 'react-icons/fa';

const ImagingModeDescriptions: Record<OSKSceneImagingMode, string> = {
    narrow: '8x8m GSD with 1km swath width',
    linescan: '8x8m GSD with 4.3km swath width',
    wide: '8x45m GSD with 4.3km swath width',
    unknown: '',
};

type SearchTopBarProps = {
    /** Available Sensor options */
    sensors?: Sensor[];
    /** List of sensors that are currently selected (for filtering) */
    selectedSensors?: Array<number>;
};

const SearchFilterTypes = [
    'Date Range',
    'Satellites',
    'Cloud Coverage',
    'Sun Azimuth',
    'Sun Elevation',
    'Off Nadir',
    'View Azimuth',
    'Imaging Mode',
    'Purchased Items',
];

const DefaultFilters = ['Date Range', 'Purchased Items'];

const LOCAL_FILTERS = 'local_filters';

const SearchTopBar = ({ sensors, selectedSensors }: SearchTopBarProps) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const map = useMap();
    const theme = useTheme() as OSKThemeType;
    const ref = useRef<HTMLDivElement>(null);
    const addFilterPanelHandle = useRef<FilterPanelButtonHandle>(null);

    const sensorOptions: ListEntry[] = (sensors ?? []).map((sensor) => {
        const { osk_id, osk_sensor_name } = sensor;
        return { label: osk_sensor_name, value: osk_id };
    });

    const imagingModeOptions: ListEntry[] = Object.values(OSKSceneImagingMode).map((mode) => ({
        label: capitalize(mode),
        value: mode,
    }));

    const [activeFilters, setActiveFilters] = useState<string[]>(DefaultFilters);
    const [searchStartDate, setSearchStartDate] = useState<NullableDate>(null);
    const [searchEndDate, setSearchEndDate] = useState<NullableDate>(new Date());
    const [dateError, setDateError] = useState<string>('');
    const [cacheSelectedSensors, setCacheSelectedSensors] = useState<number[]>(selectedSensors ?? []);
    const [cloudCover, setCloudCover] = useState<number>(100);
    const [sunAzimuth, setSunAzimuth] = useState<number[]>([0, 360]);
    const [sunElevation, setSunElevation] = useState<number[]>([-90, 90]);
    const [offNadir, setOffNadir] = useState<number[]>([0, 90]);
    const [viewAzimuth, setViewAzimuth] = useState<number[]>([0, 360]);
    const [selectedImagingModes, setSelectedImagingModes] = useState<string[]>(Object.values(OSKSceneImagingMode));
    const [purchasedItemsOnly, setPurchasedItemsOnly] = useState<boolean>(false);

    useDisableFeatureOnMouseOver(ref, 'Drag', true);
    useDisableFeatureOnMouseOver(ref, 'Zoom', true);
    useDisableFeatureOnMouseOver(ref, 'Click', true);

    useEffect(() => {
        const storedFilters = localStorage.getItem(LOCAL_FILTERS);
        if (storedFilters) {
            setActiveFilters(JSON.parse(storedFilters));
        } else {
            localStorage.setItem(LOCAL_FILTERS, JSON.stringify(DefaultFilters));
        }
    }, []);

    useEffect(() => {
        // Because it's the end date, we want to capture all tasks
        // occuring *on* the day itself. (Add 24 hours.)
        const dayMs = 1000 * 60 * 60 * 24;
        const adjustedEndDate = searchEndDate && new Date((searchEndDate?.getTime() ?? 0) + dayMs);

        dispatch(setDateRange(searchStartDate, adjustedEndDate));
    }, [searchStartDate, dispatch, searchEndDate]);

    useEffect(() => {
        sensorOptions.map((platform) => dispatch(excludePlatform(platform.value)));
        cacheSelectedSensors.map((platform) => dispatch(includePlatform(platform)));
    }, [cacheSelectedSensors]);

    useEffect(() => {
        dispatch(setCloudCoverFilter(cloudCover));
    }, [cloudCover]);

    useEffect(() => {
        dispatch(setSunAzimuthFilter(sunAzimuth));
    }, [sunAzimuth]);

    useEffect(() => {
        dispatch(setSunElevationFilter(sunElevation));
    }, [sunElevation]);

    useEffect(() => {
        dispatch(setOffNadirFilter(offNadir));
    }, [offNadir]);

    useEffect(() => {
        dispatch(setViewAzimuthFilter(viewAzimuth));
    }, [viewAzimuth]);

    useEffect(() => {
        dispatch(setImagingModeFilter(selectedImagingModes));
    }, [selectedImagingModes]);

    useEffect(() => {
        dispatch(setPurchasedItemsFilter(purchasedItemsOnly));
        dispatch(setIsSearchDirty(true));
    }, [purchasedItemsOnly]);

    const AddFilter = (filter: string) => {
        if (!SearchFilterTypes.includes(filter)) return;

        if (!activeFilters.includes(filter)) {
            const newFilters = [...activeFilters, filter];
            setActiveFilters(newFilters);
            localStorage.setItem(LOCAL_FILTERS, JSON.stringify(newFilters));
        }
    };

    const RemoveFilter = (filter: string) => {
        if (!SearchFilterTypes.includes(filter)) return;

        if (activeFilters.includes(filter)) {
            const newFilters = activeFilters.filter((f) => f !== filter);
            setActiveFilters(newFilters);
            localStorage.setItem(LOCAL_FILTERS, JSON.stringify(newFilters));
        }
    };

    const ValidateDateRange = (start: NullableDate, end: NullableDate): boolean => {
        let dateError = '';
        if ((start && searchEndDate && start > searchEndDate) || (end && searchStartDate && end < searchStartDate)) {
            dateError = 'Start date cannot be after end date.';
        }

        setDateError(dateError);

        if (dateError) return false;

        return true;
    };

    const resetDateFilters = useCallback(() => {
        setSearchStartDate(null);
        setSearchEndDate(new Date());
        dispatch(setIsSearchDirty(true));
    }, [setSearchStartDate, setSearchEndDate]);

    const resetSensorFilters = useCallback(() => {
        setCacheSelectedSensors(sensors?.map((sensor) => sensor.osk_id) ?? []);
        dispatch(setIsSearchDirty(true));
    }, [setCacheSelectedSensors, sensors]);

    const resetCloudCoverFilters = useCallback(() => {
        setCloudCover(100);
        dispatch(setIsSearchDirty(true));
    }, [setCloudCover, cloudCover]);

    const resetSunAzimuthFilters = useCallback(() => {
        setSunAzimuth([0, 360]);
        dispatch(setIsSearchDirty(true));
    }, [setSunAzimuth, sunAzimuth]);

    const resetSunElevationFilters = useCallback(() => {
        setSunElevation([-90, 90]);
        dispatch(setIsSearchDirty(true));
    }, [setSunElevation, sunElevation]);

    const resetOffNadirFilters = useCallback(() => {
        setOffNadir([0, 90]);
        dispatch(setIsSearchDirty(true));
    }, [setOffNadir, offNadir]);

    const resetViewAzimuthFilters = useCallback(() => {
        setViewAzimuth([0, 360]);
        dispatch(setIsSearchDirty(true));
    }, [setViewAzimuth, viewAzimuth]);

    const resetImagingModeFilters = useCallback(() => {
        setSelectedImagingModes(Object.values(OSKSceneImagingMode));
        dispatch(setIsSearchDirty(true));
    }, [setSelectedImagingModes, selectedImagingModes]);

    const resetPurchasedItemsOnlyFilters = useCallback(() => {
        setPurchasedItemsOnly(false);
        dispatch(setIsSearchDirty(true));
    }, [setPurchasedItemsOnly, purchasedItemsOnly]);

    return (
        <Box style={{ position: 'relative', pointerEvents: 'auto' }} ref={ref} id="search-top-bar">
            <Box
                style={{
                    width: '100%',
                    minHeight: '50px',
                    maxHeight: '150px',
                    zIndex: GlobalZIndex.Overlay + 1,
                    backgroundColor: 'white',
                    padding: '8px 18px 0px 18px',
                    overflow: 'hidden',
                }}
                grow
            >
                <Box style={{ height: 'fit-content', width: '100%' }} row>
                    <GeocodingSearch
                        style={{ width: '315px' }}
                        placeholder="Navigate to GPS, location name, or task id"
                        onSelected={(location, bounds) => {
                            const url = `/map/@${location.lat},${location.lng}`;
                            navigate(url, { replace: true });

                            if (bounds) {
                                // If we have a boundary, let's fit the whole thing
                                const latLngBounds = L.latLngBounds(bounds.southwest, bounds.northeast);
                                map.fitCoordinates([OSKGeoJson.fromLatLngBounds(latLngBounds)], 0.1);
                            } else {
                                // Otherwise, if we just have a point that's ok too.
                                const coord = { lat: location.lat, lng: location.lng } as LatLng;
                                map.fitCoordinates([OSKGeoJson.fromCoordinate(coord)], 0.1);
                            }
                        }}
                        onQueryChanged={(query) => {
                            const cleanQuery = ensureUUIDFormat(query);

                            // Best guess if it's a UUID
                            if (cleanQuery.length === 36 && cleanQuery.includes('-')) {
                                dispatch<any>(doSearchAsyncForTaskId(cleanQuery, false));
                            }
                        }}
                    />
                    <Box
                        style={{
                            backgroundColor: theme.colors.black200,
                            minWidth: '1px',
                            maxWidth: '1px',
                            margin: '0px 10px 0px 16px',
                        }}
                        grow
                    />
                    <Box style={{ justifyContent: 'space-between', height: '100%' }} grow>
                        <Box style={{ flexWrap: 'wrap' }}>
                            {/* Date Range Filter */}
                            <FilterPanelButton
                                visible={activeFilters.includes('Date Range')}
                                showClear={searchStartDate !== null && searchEndDate !== null}
                                clearFilter={() => resetDateFilters()}
                                panel={
                                    <DropdownFilterPanel
                                        title="Date"
                                        description="Select the date range to search within."
                                        onResetFilter={() => resetDateFilters()}
                                    >
                                        <Box style={{ justifyContent: 'space-between', paddingTop: '8px' }}>
                                            <DatePicker
                                                variant="contrast"
                                                name="start-date"
                                                defaultValue={searchStartDate}
                                                style={{ marginRight: '2px' }}
                                                validate={(date) => {
                                                    return ValidateDateRange(date, null);
                                                }}
                                                onChange={(newDate: NullableDate) => {
                                                    setSearchStartDate(newDate);
                                                    if (ValidateDateRange(newDate, null)) {
                                                        dispatch(setIsSearchDirty(true));
                                                    } else {
                                                        setSearchStartDate(null);
                                                    }
                                                }}
                                            />
                                            <DatePicker
                                                variant="contrast"
                                                name="end-date"
                                                defaultValue={searchEndDate}
                                                validate={(date) => {
                                                    return ValidateDateRange(null, date);
                                                }}
                                                onChange={(newDate: NullableDate) => {
                                                    setSearchEndDate(newDate);
                                                    if (ValidateDateRange(null, newDate)) {
                                                        dispatch(setIsSearchDirty(true));
                                                    } else {
                                                        setSearchEndDate(null);
                                                    }
                                                }}
                                            />
                                        </Box>
                                        {dateError && (
                                            <Typography variant="body3" color="red" vm={8}>
                                                {dateError}
                                            </Typography>
                                        )}
                                    </DropdownFilterPanel>
                                }
                                icon="calendar"
                            >
                                <Typography variant="caption1" style={{ minWidth: '130px' }}>
                                    {searchStartDate || searchEndDate
                                        ? (() => {
                                              const from = date_format(searchStartDate);
                                              const to = date_format(searchEndDate);
                                              return `${from ? from : 'All'} - ${to ? to : 'Present'}`;
                                          })()
                                        : 'Date Range'}
                                </Typography>
                            </FilterPanelButton>
                            {/* Purchased Items Filter */}
                            <FilterPanelButton
                                visible={activeFilters.includes('Purchased Items')}
                                showExpand={false}
                                showClear={false}
                                onClick={() => setPurchasedItemsOnly(!purchasedItemsOnly)}
                                showBorder={!purchasedItemsOnly}
                                {...(purchasedItemsOnly && { bgColor: theme.colors.green900 })}
                            >
                                <Box center="vertical" style={{ minWidth: '100px' }}>
                                    <OSKIcon
                                        style={{ marginRight: '8px' }}
                                        code="shopping-bag"
                                        fill={purchasedItemsOnly ? theme.colors.white : 'black'}
                                    />
                                    <Typography
                                        variant="caption1"
                                        color={purchasedItemsOnly ? theme.colors.white : 'black'}
                                    >
                                        Purchased Items
                                    </Typography>
                                    {purchasedItemsOnly ? (
                                        <OSKIcon
                                            style={{ marginLeft: '8px' }}
                                            code="check-circle"
                                            fill={theme.colors.white}
                                        />
                                    ) : (
                                        <Box
                                            style={{
                                                marginLeft: '8px',
                                                border: `1px solid ${theme.colors.black400}`,
                                                borderRadius: '15px',
                                                marginRight: '1px',
                                            }}
                                            w={15}
                                            h={15}
                                        />
                                    )}
                                </Box>
                            </FilterPanelButton>
                            {/* Satellites Filter */}
                            <FilterPanelButton
                                visible={activeFilters.includes('Satellites')}
                                showClear
                                clearFilter={() => {
                                    RemoveFilter('Satellites');
                                    resetSensorFilters();
                                }}
                                panel={
                                    <DropdownFilterPanel
                                        title="Satellites"
                                        description="Select which satellites to search."
                                        onResetFilter={() => resetSensorFilters()}
                                    >
                                        {sensorOptions.map((sensor) => (
                                            <Checkbox
                                                scale={150}
                                                key={`sensor-${sensor.value}`}
                                                defaultChecked={cacheSelectedSensors.includes(sensor.value)}
                                                onChange={(e) => {
                                                    dispatch(setIsSearchDirty(true));

                                                    if (cacheSelectedSensors.includes(sensor.value)) {
                                                        setCacheSelectedSensors(
                                                            cacheSelectedSensors.filter((id) => id !== sensor.value),
                                                        );
                                                    } else {
                                                        setCacheSelectedSensors([
                                                            ...cacheSelectedSensors,
                                                            sensor.value,
                                                        ]);
                                                    }
                                                }}
                                            >
                                                <Typography variant="body2" style={{ padding: '4px 8px' }}>
                                                    {sensor.label}
                                                </Typography>
                                            </Checkbox>
                                        ))}
                                    </DropdownFilterPanel>
                                }
                                icon="satellite"
                            >
                                <Box center="vertical" style={{ minWidth: '75px' }}>
                                    <Typography variant="caption1">Satellites</Typography>
                                    {cacheSelectedSensors && cacheSelectedSensors?.length > 0 && (
                                        <Box
                                            style={{
                                                padding: '4px',
                                                backgroundColor: theme.colors.accent,
                                                color: 'white',
                                                borderRadius: '100px',
                                                width: '18px',
                                                height: '18px',
                                                marginLeft: '8px',
                                                marginRight: '-4px',
                                            }}
                                            center="all"
                                        >
                                            <Typography variant="caption3">{cacheSelectedSensors?.length}</Typography>
                                        </Box>
                                    )}
                                </Box>
                            </FilterPanelButton>
                            {/* Cloud Coverage Filter */}
                            <FilterPanelButton
                                visible={activeFilters.includes('Cloud Coverage')}
                                showClear
                                clearFilter={() => {
                                    RemoveFilter('Cloud Coverage');
                                    resetCloudCoverFilters();
                                }}
                                panel={
                                    <DropdownFilterPanel
                                        title="Cloud Coverage"
                                        description="Use the slider to specify the acceptable amount of cloud coverage."
                                        onResetFilter={() => resetCloudCoverFilters()}
                                    >
                                        <Box
                                            center="all"
                                            style={{
                                                width: '100%',
                                                minWidth: '300px',
                                                padding: '0px 0px 14px 0px',
                                            }}
                                        >
                                            <Slider
                                                defaultMinValue={0}
                                                defaultMaxValue={cloudCover}
                                                freezeMinValue
                                                minValue={0}
                                                maxValue={100}
                                                decimals={0}
                                                unit="%"
                                                minValueLabel={
                                                    <Box col center="horizontal" w={20}>
                                                        <span style={{ margin: '2px 0px' }}>0%</span>
                                                        <FaSun />
                                                    </Box>
                                                }
                                                maxValueLabel={
                                                    <Box col center="horizontal" w={20}>
                                                        <span style={{ margin: '2px 0px' }}>100%</span>
                                                        <FaCloud />
                                                    </Box>
                                                }
                                                variant="action"
                                                onMaxValueChange={(newValue) => {
                                                    dispatch(setIsSearchDirty(true));
                                                    setCloudCover(newValue as number);
                                                }}
                                            />
                                        </Box>
                                    </DropdownFilterPanel>
                                }
                                icon="cloud"
                            >
                                <Box style={{ minWidth: '130px' }} center="vertical">
                                    <Typography variant="caption1">Cloud Coverage</Typography>
                                    <Box
                                        style={{
                                            padding: '4px',
                                            backgroundColor: theme.colors.accent,
                                            color: 'white',
                                            borderRadius: '100px',
                                            height: '18px',
                                            marginLeft: '8px',
                                            marginRight: '-4px',
                                        }}
                                        center="all"
                                    >
                                        <Typography variant="caption3">
                                            {cloudCover ? <>&lt; {cloudCover}%</> : 'None'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </FilterPanelButton>
                            {/* Sun Azimuth Filter */}
                            <FilterPanelButton
                                visible={activeFilters.includes('Sun Azimuth')}
                                showClear
                                clearFilter={() => {
                                    RemoveFilter('Sun Azimuth');
                                    resetSunAzimuthFilters();
                                }}
                                panel={
                                    <DropdownFilterPanel
                                        title="Sun Azimuth Angle"
                                        description="Use the slider to define the range of Sun Azimuth"
                                        onResetFilter={() => resetSunAzimuthFilters()}
                                    >
                                        <Box
                                            center="all"
                                            style={{
                                                width: '100%',
                                                minWidth: '300px',
                                                padding: '0px 0px 14px 0px',
                                            }}
                                        >
                                            <Slider
                                                minValue={0}
                                                maxValue={360}
                                                defaultMinValue={sunAzimuth[0]}
                                                defaultMaxValue={sunAzimuth[1]}
                                                decimals={0}
                                                unit="&deg;"
                                                variant="action"
                                                onMinValueChange={(newValue) => {
                                                    dispatch(setIsSearchDirty(true));
                                                    setSunAzimuth([newValue, sunAzimuth[1]]);
                                                }}
                                                onMaxValueChange={(newValue) => {
                                                    dispatch(setIsSearchDirty(true));
                                                    setSunAzimuth([sunAzimuth[0], newValue]);
                                                }}
                                            />
                                        </Box>
                                    </DropdownFilterPanel>
                                }
                                icon="sun-azimuth"
                            >
                                <Box style={{ minWidth: '130px' }} center="vertical">
                                    <Typography variant="caption1">Sun Azimuth</Typography>
                                    <Box
                                        style={{
                                            padding: '4px',
                                            backgroundColor: theme.colors.accent,
                                            color: 'white',
                                            borderRadius: '100px',
                                            height: '18px',
                                            marginLeft: '8px',
                                            marginRight: '-4px',
                                        }}
                                        center="all"
                                    >
                                        <Typography variant="caption3">
                                            {sunAzimuth[0]}&deg; - {sunAzimuth[1]}&deg;
                                        </Typography>
                                    </Box>
                                </Box>
                            </FilterPanelButton>
                            {/* Sun Elevation Filter */}
                            <FilterPanelButton
                                visible={activeFilters.includes('Sun Elevation')}
                                showClear
                                clearFilter={() => {
                                    RemoveFilter('Sun Elevation');
                                    resetSunElevationFilters();
                                }}
                                panel={
                                    <DropdownFilterPanel
                                        title="Sun Elevation Angle"
                                        description="Use the slider to define the Sun Elevation Angle"
                                        onResetFilter={() => resetSunElevationFilters()}
                                    >
                                        <Box
                                            center="all"
                                            style={{
                                                width: '100%',
                                                minWidth: '300px',
                                                padding: '0px 0px 14px 0px',
                                            }}
                                        >
                                            <Slider
                                                minValue={-90}
                                                maxValue={90}
                                                defaultMinValue={sunElevation[0]}
                                                defaultMaxValue={sunElevation[1]}
                                                decimals={0}
                                                unit="&deg;"
                                                variant="action"
                                                onMinValueChange={(newValue) => {
                                                    dispatch(setIsSearchDirty(true));
                                                    setSunElevation([newValue, sunElevation[1]]);
                                                }}
                                                onMaxValueChange={(newValue) => {
                                                    dispatch(setIsSearchDirty(true));
                                                    setSunElevation([sunElevation[0], newValue]);
                                                }}
                                            />
                                        </Box>
                                    </DropdownFilterPanel>
                                }
                                icon="sun-elevation"
                            >
                                <Box style={{ minWidth: '130px' }} center="vertical">
                                    <Typography variant="caption1">Sun Elevation</Typography>
                                    <Box
                                        style={{
                                            padding: '4px',
                                            backgroundColor: theme.colors.accent,
                                            color: 'white',
                                            borderRadius: '100px',
                                            height: '18px',
                                            marginLeft: '8px',
                                            marginRight: '-4px',
                                        }}
                                        center="all"
                                    >
                                        <Typography variant="caption3">
                                            {sunElevation[0]}&deg; - {sunElevation[1]}&deg;
                                        </Typography>
                                    </Box>
                                </Box>
                            </FilterPanelButton>
                            {/* Off Nadir Filter */}
                            <FilterPanelButton
                                visible={activeFilters.includes('Off Nadir')}
                                showClear
                                clearFilter={() => {
                                    RemoveFilter('Off Nadir');
                                    resetOffNadirFilters();
                                }}
                                panel={
                                    <DropdownFilterPanel
                                        title="Off Nadir Angle"
                                        description="Use the slider to define the angle from the sensor and the scene"
                                        onResetFilter={() => resetOffNadirFilters()}
                                    >
                                        <Box
                                            center="all"
                                            style={{
                                                width: '100%',
                                                minWidth: '300px',
                                                padding: '0px 0px 14px 0px',
                                            }}
                                        >
                                            <Slider
                                                minValue={0}
                                                maxValue={90}
                                                defaultMinValue={offNadir[0]}
                                                defaultMaxValue={offNadir[1]}
                                                decimals={1}
                                                unit="&deg;"
                                                variant="action"
                                                onMinValueChange={(newValue) => {
                                                    dispatch(setIsSearchDirty(true));
                                                    setOffNadir([newValue, offNadir[1]]);
                                                }}
                                                onMaxValueChange={(newValue) => {
                                                    dispatch(setIsSearchDirty(true));
                                                    setOffNadir([offNadir[0], newValue]);
                                                }}
                                            />
                                        </Box>
                                    </DropdownFilterPanel>
                                }
                                icon="off-nadir"
                            >
                                <Box style={{ minWidth: '105px' }} center="vertical">
                                    <Typography variant="caption1">Off Nadir</Typography>
                                    <Box
                                        style={{
                                            padding: '4px',
                                            backgroundColor: theme.colors.accent,
                                            color: 'white',
                                            borderRadius: '100px',
                                            height: '18px',
                                            marginLeft: '8px',
                                            marginRight: '-4px',
                                        }}
                                        center="all"
                                    >
                                        <Typography variant="caption3">
                                            {offNadir[0]}&deg; - {offNadir[1]}&deg;
                                        </Typography>
                                    </Box>
                                </Box>
                            </FilterPanelButton>
                            {/* Viewing Azimuth Filter */}
                            <FilterPanelButton
                                visible={activeFilters.includes('View Azimuth')}
                                showClear
                                clearFilter={() => {
                                    RemoveFilter('View Azimuth');
                                    resetViewAzimuthFilters();
                                }}
                                panel={
                                    <DropdownFilterPanel
                                        title="Viewing Azimuth Angle"
                                        description="Use the slider to define the Azimuth View"
                                        onResetFilter={() => resetViewAzimuthFilters()}
                                    >
                                        <Box
                                            center="all"
                                            style={{
                                                width: '100%',
                                                minWidth: '300px',
                                                padding: '0px 0px 14px 0px',
                                            }}
                                        >
                                            <Slider
                                                minValue={0}
                                                maxValue={360}
                                                defaultMinValue={viewAzimuth[0]}
                                                defaultMaxValue={viewAzimuth[1]}
                                                decimals={0}
                                                unit="&deg;"
                                                variant="action"
                                                onMinValueChange={(newValue) => {
                                                    dispatch(setIsSearchDirty(true));
                                                    setViewAzimuth([newValue, viewAzimuth[1]]);
                                                }}
                                                onMaxValueChange={(newValue) => {
                                                    dispatch(setIsSearchDirty(true));
                                                    setViewAzimuth([viewAzimuth[0], newValue]);
                                                }}
                                            />
                                        </Box>
                                    </DropdownFilterPanel>
                                }
                                icon="azimuth"
                            >
                                <Box style={{ minWidth: '135px' }} center="vertical">
                                    <Typography variant="caption1">View Azimuth</Typography>
                                    <Box
                                        style={{
                                            padding: '4px',
                                            backgroundColor: theme.colors.accent,
                                            color: 'white',
                                            borderRadius: '100px',
                                            height: '18px',
                                            marginLeft: '8px',
                                            marginRight: '-4px',
                                        }}
                                        center="all"
                                    >
                                        <Typography variant="caption3">
                                            {viewAzimuth[0]}&deg; - {viewAzimuth[1]}&deg;
                                        </Typography>
                                    </Box>
                                </Box>
                            </FilterPanelButton>
                            {/* Imaging Mode Filter */}
                            <FilterPanelButton
                                visible={activeFilters.includes('Imaging Mode')}
                                showClear
                                clearFilter={() => {
                                    RemoveFilter('Imaging Mode');
                                    resetImagingModeFilters();
                                }}
                                panel={
                                    <DropdownFilterPanel
                                        title="Imaging Mode"
                                        description="Select which imaging modes to search."
                                        onResetFilter={() => resetImagingModeFilters()}
                                    >
                                        {imagingModeOptions.map((mode) => (
                                            <Checkbox
                                                scale={150}
                                                key={`imgmode-${mode.value}`}
                                                defaultChecked={selectedImagingModes.includes(mode.value)}
                                                onChange={(e) => {
                                                    dispatch(setIsSearchDirty(true));

                                                    if (selectedImagingModes.includes(mode.value)) {
                                                        setSelectedImagingModes(
                                                            selectedImagingModes.filter((m) => m !== mode.value),
                                                        );
                                                    } else {
                                                        setSelectedImagingModes([...selectedImagingModes, mode.value]);
                                                    }
                                                }}
                                            >
                                                <Box center="vertical">
                                                    <Typography
                                                        variant="body2"
                                                        style={{ padding: '8px 0px 8px 12px' }}
                                                        strong
                                                    >
                                                        {mode.label}
                                                    </Typography>
                                                    {ImagingModeDescriptions[mode.value as OSKSceneImagingMode] ? (
                                                        <Typography variant="body3">
                                                            &nbsp;-&nbsp;
                                                            {ImagingModeDescriptions[mode.value as OSKSceneImagingMode]}
                                                        </Typography>
                                                    ) : (
                                                        <></>
                                                    )}
                                                </Box>
                                            </Checkbox>
                                        ))}
                                    </DropdownFilterPanel>
                                }
                                icon="camera"
                            >
                                <Box center="vertical" style={{ minWidth: '100px' }}>
                                    <Typography variant="caption1">Imaging Mode</Typography>
                                    {selectedImagingModes && selectedImagingModes?.length > 0 && (
                                        <Box
                                            style={{
                                                padding: '4px',
                                                backgroundColor: theme.colors.accent,
                                                color: 'white',
                                                borderRadius: '100px',
                                                width: '18px',
                                                height: '18px',
                                                marginLeft: '8px',
                                                marginRight: '-4px',
                                            }}
                                            center="all"
                                        >
                                            <Typography variant="caption3">{selectedImagingModes?.length}</Typography>
                                        </Box>
                                    )}
                                </Box>
                            </FilterPanelButton>
                            {SearchFilterTypes.filter((filter) => !activeFilters.includes(filter)).length > 0 && (
                                <FilterPanelButton
                                    ref={addFilterPanelHandle}
                                    showExpand={false}
                                    showClear={false}
                                    showBorder={false}
                                    panel={
                                        <DropdownFilterPanel>
                                            <Box center="horizontal" col>
                                                {SearchFilterTypes.filter(
                                                    (filter) => !activeFilters.includes(filter),
                                                ).map((filter) => (
                                                    <Button
                                                        key={`filter-button-${filter}`}
                                                        variant="contrast"
                                                        width="100%"
                                                        onClick={() => {
                                                            AddFilter(filter);
                                                            addFilterPanelHandle.current?.closePanel();
                                                        }}
                                                    >
                                                        {filter}
                                                    </Button>
                                                ))}
                                            </Box>
                                        </DropdownFilterPanel>
                                    }
                                >
                                    <Typography variant="caption1">+ Add Filter</Typography>
                                </FilterPanelButton>
                            )}
                            {/* End filters */}
                        </Box>
                        <Box style={{ height: '100%' }}>
                            <Button
                                variant="contrast"
                                style={{ alignSelf: 'flex-end', height: '36px', minWidth: '115px' }}
                                onClick={() => {
                                    resetDateFilters();
                                    resetSensorFilters();
                                    resetCloudCoverFilters();
                                    resetSunAzimuthFilters();
                                    resetSunElevationFilters();
                                    resetOffNadirFilters();
                                    resetViewAzimuthFilters();
                                    resetImagingModeFilters();
                                    resetPurchasedItemsOnlyFilters();

                                    setActiveFilters(DefaultFilters);
                                }}
                            >
                                <Typography variant="caption1">Reset Filters</Typography>
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

const mapStateToProps = (state: RootState) => {
    const accessibleSensors = state.osk.sensors.filter((sensor) => state.osk.accessibleSensors.includes(sensor.osk_id));
    const selectedSensors = state.data.search.platforms.filter((platform) =>
        state.osk.accessibleSensors.includes(platform),
    );
    return {
        sensors: accessibleSensors,
        selectedSensors,
    };
};

export default connect(mapStateToProps, noop)(SearchTopBar);
